import {
  collection,
  doc,
  getDocs,
  getDoc,
  increment,
  onSnapshot,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  Timestamp,
  type DocumentData,
  type QueryConstraint,
  type QueryDocumentSnapshot,

  
} from 'firebase/firestore';
import {db, auth, storage} from '../config/firebase';
import {ref, deleteObject} from 'firebase/storage';
import type { User } from 'firebase/auth';
import { Prompt, Category } from '../types';
import {ACCOUNT_STATUS} from '../constants/accountStatus';
import { DEFAULT_SUBSCRIPTION_DAYS, DEFAULT_PLAN, DEFAULT_BILLING_CYCLE, INITIAL_RENEWAL_COUNT } from '../constants/subscription';
import type { UserData }from '../types/UserData';
import {
  manifesto,
  codeOfConduct,
  termsOfUse,
  privacyPolicy,
  subscriptionPolicy,
} from '../legal/documents';
import type { LegalAcceptance } from '../legal';
// Mock data for when Firebase is not configured
const mockPrompts: Prompt[] = [
  {
    id: '1',
    content: 'Conteúdo do prompt de exemplo...',
    category: 'Geral',
    categoryId: 'geral',
    categoryName: 'Geral',
    observation: 'Este é um exemplo de prompt de teste.',
    imageUrl: '',
    likes: 0,
    userId: 'mock-user-1',
    isPublic: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  },
  {
    id: '2',
    content: 'Crie uma imagem de um gato astronauta no espaço',
    category: 'Criativo',
    categoryId: 'criativo',
    categoryName: 'Criativo',
    observation: 'Prompt para geração de imagens criativas',
    variations: [
      'Um gato astronauta flutuando no espaço sideral com estrelas ao fundo',
      'Gato astronauta em traje espacial completo, com lua visível ao fundo',
      'Ilustração de gato como astronauta explorando planetas alienígenas'
    ],
    likes: 5,
    userId: 'mock-user-1',
    isPublic: true,
    createdAt: new Date(Date.now() - 86400000), // 1 dia atrás
    updatedAt: new Date(Date.now() - 86400000),
  },
];

const mockCategories: Category[] = [
  { id: 'todos', name: 'Todos', color: '#6B7280' },
  { id: '1', name: 'Geral', color: '#8B5CF6' },
  { id: '2', name: 'Criativo', color: '#3B82F6' },
  { id: '3', name: 'Técnico', color: '#10B981' },
];

// Helper function to check if Firebase is available
const isFirebaseAvailable = () => !!db;

const markerPattern = /^[a-z][a-z0-9]{1,9}$/;
const markerDigitLimitPattern = /^[a-z]*\d?[a-z]*\d?[a-z]*\d?[a-z]*$/;

export const normalizeMarker = (marker: string): string =>
  marker.trim().toLowerCase();

export const isValidMarker = (marker: string): boolean =>
  markerPattern.test(marker)
  && markerDigitLimitPattern.test(marker);

const normalizeMarkers = (markers: unknown): string[] | undefined => {
  if (markers === undefined) return undefined;
  if (!Array.isArray(markers)) return undefined;

  return markers.map((marker) =>
    typeof marker === 'string' ? normalizeMarker(marker) : marker
  ) as string[];
};

const hasValidMarkers = (markers: unknown): boolean => {
  if (markers === undefined) return true;

  return Array.isArray(markers)
    && markers.length <= 3
    && markers.every(
      (marker) => typeof marker === 'string' && isValidMarker(marker)
    )
    && new Set(markers).size === markers.length;
};

/**
 * Valida dados do prompt antes do salvamento
 * @param data Dados do prompt a serem validados
 * @returns Objeto com { isValid: boolean, errors: string[] }
 */
export const validatePromptData = (
  data: Partial<Prompt>,
  allowUnchangedLegacyMarkers: boolean = false
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Validações obrigatórias
  if (!data.content || typeof data.content !== 'string' || data.content.trim().length === 0) {
    errors.push('Conteúdo do prompt é obrigatório');
  }

  if (
  !data.categoryName ||
  typeof data.categoryName !== 'string' ||
  data.categoryName.trim().length === 0
) {
  errors.push(
    'Categoria é obrigatória'
  );
}

  // Validações opcionais mas com formato correto
  if (data.imageUrl !== undefined && typeof data.imageUrl !== 'string') {
    errors.push('URL da imagem deve ser uma string válida');
  }

  if (data.observation !== undefined && typeof data.observation !== 'string') {
    errors.push('Observação deve ser uma string');
  }

  if (data.identifier !== undefined && typeof data.identifier !== 'string') {
    errors.push('Identificador deve ser uma string');
  }


  if (data.variations !== undefined) {
    if (!Array.isArray(data.variations)) {
      errors.push('Variações deve ser um array');
    } else {
      data.variations.forEach((variation, index) => {
        if (typeof variation !== 'string') {
          errors.push(`Variação ${index + 1} deve ser uma string`);
        }
      });
    }
  }

  if (!allowUnchangedLegacyMarkers && !hasValidMarkers(data.markers)) {
    errors.push('Marcadores inválidos');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Limpa dados do prompt removendo valores undefined e strings vazias
 * @param data Dados do prompt a serem limpos
 * @returns Dados limpos prontos para salvar
 */
export const cleanPromptData = (data: Partial<Prompt>): Record<string, any> => {
  const cleaned: Record<string, any> = {};

  Object.entries(data).forEach(([key, value]) => {
    // Remove valores undefined
    if (value === undefined) {
      return;
    }

    // Remove strings vazias para campos opcionais
    if (typeof value === 'string' && value.trim().length === 0) {
      // Campos que podem ser vazios: observation, imageUrl, identifier, author
      if (['observation', 'imageUrl', 'identifier', 'author'].includes(key)) {
        return;
      }
    }

    // Limpa variações removendo strings vazias
    if (key === 'variations' && Array.isArray(value)) {
      const cleanedVariations = value
        .filter((variation): variation is string => typeof variation === 'string')
        .map(variation => variation.trim())
        .filter(variation => variation.length > 0);

      if (cleanedVariations.length > 0) {
        cleaned[key] = cleanedVariations;
      }
      return;
    }

    // Mantém valores válidos
    cleaned[key] = value;
  });

  return cleaned;
};

export async function getUserByUid(
  uid: string
): Promise<UserData | null> {

  try {

    const userRef =
      doc(
      db,
     'users',
      uid
      );

    const userSnap =
      await getDoc(
        userRef
      );

    if (
      !userSnap.exists()
    ) {

      return null;
    }

    return {

      id: userSnap.id,
      ...(userSnap.data() as Omit<UserData, 'id'>)

    };

  } catch (error) {

    console.error(
      'Erro ao buscar usuário:',
      error
    );

    return null;
  }
}

export function subscribeToUser(
  uid: string,
  callback: (
    userData: UserData | null
  ) => void
) {

  const userRef = doc(
    db,
    'users',
    uid
  );

  return onSnapshot(
    userRef,
    (snapshot) => {

      if (!snapshot.exists()) {

        callback(null);

        return;
      }

      callback({

        id: snapshot.id,

        ...(snapshot.data() as Omit<
          UserData,
          'id'
        >)

      });

    },
    (error) => {

      console.error(
        'Erro ao escutar usuário:',
        error
      );

      callback(null);

    }
  );
}

/**
 * Salva prompt de forma segura com validação e limpeza
 * @param data Dados do prompt
 * @param userId ID do usuário que está salvando
 * @param isPublic Se o prompt deve ser público
 * @returns Promise com resultado do salvamento
 */
export const savePrompt = async (
  data: Omit<Prompt, 'id' | 'createdAt' | 'updatedAt' | 'userId' | 'isPublic'>,
  userId: string,
  isPublic: boolean = false,
  promptId?: string
): Promise<{ success: boolean; error?: string; prompt?: Prompt }> => {
  try {
    let existingMarkers: unknown;

    if (isFirebaseAvailable() && promptId) {
      const existingPrompt = await getDoc(doc(db, 'prompts', promptId));
      existingMarkers = existingPrompt.exists()
        ? existingPrompt.data().markers
        : undefined;
    }

    const markersAreUnchanged = promptId !== undefined
      && JSON.stringify(data.markers ?? []) === JSON.stringify(existingMarkers ?? []);

    const normalizedMarkers = markersAreUnchanged
      ? data.markers
      : normalizeMarkers(data.markers);

    // 1. Valida dados
    const validation = validatePromptData(
      { ...data, markers: normalizedMarkers },
      markersAreUnchanged
    );
    if (!validation.isValid) {
      return {
        success: false,
        error: `Dados inválidos: ${validation.errors.join(', ')}`
      };
    }

    // 2. Limpa dados
  const cleanedData =
  sanitizePromptData(
    cleanPromptData({ ...data, markers: normalizedMarkers })
  );

  let categoryData;

    if (isFirebaseAvailable()) {

      if (isPublic) {

  const categoryRef = doc(
    db,
    'categories',
    cleanedData.categoryId!
  );

  const categorySnap =
    await getDoc(categoryRef);

  if (!categorySnap.exists()) {

    throw new Error(
      'Categoria não encontrada.'
    );
  }

  categoryData = {

    categoryId:
      categorySnap.id,

    categoryName:
      categorySnap.data().name

  };

} else {

  categoryData =
    await ensureCategoryExists(

      cleanedData.categoryName!,

      userId,

      false

    );

}
      // 3a. Se tem ID, faz UPDATE do documento existente
      if (promptId) {
        const dataToUpdate = {
  ...cleanedData,

  categoryId:
    categoryData.categoryId,

  categoryName:
    categoryData.categoryName,

  variations:
    cleanedData.variations || [],

  userId,

  isPublic,

  isSensitive:
    cleanedData.isSensitive || false,

  updatedAt:
    serverTimestamp(),
};

        const promptRef = doc(db, 'prompts', promptId);
        await updateDoc(promptRef, dataToUpdate);

        const savedPrompt: Prompt = {
          ...dataToUpdate,
          id: promptId,
          createdAt: new Date(), // será sobrescrito com o valor real no cliente
        } as Prompt;

        return {
          success: true,
          prompt: savedPrompt
        };
      } else {
  const promptsRef =
  collection(db, 'prompts');

const fifteenSecondsAgo =
  new Date(Date.now() - 15000);

const recentPromptsQuery = query(
  promptsRef,

  where('userId', '==', userId),

  where(
    'createdAt',
    '>=',
    fifteenSecondsAgo
  ),

  orderBy('createdAt', 'desc'),

  limit(2)
);

const recentPrompts =
  await getDocs(recentPromptsQuery);

if (recentPrompts.size >= 2) {

  return {
    success: false,
    error:
      'Espere alguns segundos antes de criar novos prompts.'
  };
}
        // 3b. Senão, cria novo documento

let categoryData;

if (isPublic) {

  const categoryRef = doc(
    db,
    'categories',
    cleanedData.categoryId!
  );

  const categorySnap =
    await getDoc(categoryRef);

  if (!categorySnap.exists()) {

    throw new Error(
      'Categoria não encontrada.'
    );
  }

  categoryData = {

    categoryId:
      categorySnap.id,

    categoryName:
      categorySnap.data().name

  };

} else {

  categoryData =
    await ensureCategoryExists(

      cleanedData.categoryName!,

      userId,

      false

    );

}


        const dataToSave = {
          ...cleanedData,

categoryId:
  categoryData.categoryId,

categoryName:
  categoryData.categoryName,

          variations:
          
  cleanedData.variations || [],
          userId,
          isPublic,
          isHidden: false,
          isSensitive:
  cleanedData.isSensitive || false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          author:
  auth.currentUser?.displayName
  || 'Usuário',
        };


console.log(
  'PROMPT ENVIADO',
  JSON.stringify(dataToSave, null, 2)
);

        const docRef = await addDoc(collection(db, 'prompts'), dataToSave);
        const savedPrompt: Prompt = {
          ...dataToSave,
          id: docRef.id,
        } as Prompt;

        return {
          success: true,
          prompt: savedPrompt
        };
      }
    } else {
      // Simula salvamento para desenvolvimento
      if (promptId) {
        // Simula update em mock data
        const dataToUpdate = {
          ...cleanedData,
          userId,
          isPublic,
          updatedAt: serverTimestamp(),
        };

        const mockIndex = mockPrompts.findIndex(p => p.id === promptId);
        if (mockIndex !== -1) {
          mockPrompts[mockIndex] = {
            ...mockPrompts[mockIndex],
            ...dataToUpdate,
          };
        }

        return {
          success: true,
          prompt: mockPrompts[mockIndex] as Prompt
        };
      } else {
        // Simula novo prompt
        console.log('Firebase não configurado - simulando salvamento:', cleanedData);

        const mockPrompt: Prompt = {
          ...cleanedData,
          userId,
          isPublic,
          id: `mock-${Date.now()}`,
          createdAt: new Date(),
          updatedAt: new Date(),
        } as Prompt;

        mockPrompts.push(mockPrompt);

        return {
          success: true,
          prompt: mockPrompt
        };
      }
    }
  } catch (error) {
    console.error('Erro ao salvar prompt:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro desconhecido ao salvar'
    };
  }
};

// Prompts
export async function getPrompts(filters?: {
  category?: string;
  search?: string;
  sortBy?: 'newest' | 'oldest' | 'popular';
  mode?: 'global' | 'personal'; // Novo filtro para modo
  userId?: string; // ID do usuário para modo pessoal
}): Promise<Prompt[]> {
  if (!isFirebaseAvailable()) {
    console.log('Using mock data - Firebase not configured');
    let prompts = [...mockPrompts];

    // Apply filters to mock data
    if (filters?.category) {
  prompts = prompts.filter(
    p => p.categoryName === filters.category
  );
}

    // Filtrar por modo (global/personal)
    if (
  filters?.mode === 'personal'
  &&
  filters?.userId
) {

  prompts = prompts.filter(
    p =>
      p.userId === filters.userId
      &&
      p.isPublic === false
  );

}
    else if (filters?.mode === 'global') {
      prompts = prompts.filter(p => p.isPublic === true);
    }

    if (filters?.search) {
      const searchLower = filters.search.toLowerCase();
      prompts = prompts.filter(
  (p) =>

    p.content
      .toLowerCase()
      .includes(searchLower)

    ||

    (
      p.categoryName
        ?.toLowerCase()
        .includes(searchLower)
        ?? false
    )

    ||

    (
      p.identifier
        ?.toLowerCase()
        .includes(searchLower)
        ?? false
    )

    ||

    (
      p.observation
        ?.toLowerCase()
        .includes(searchLower)
        ?? false
    )

    ||

    (
      p.author
        ?.toLowerCase()
        .includes(searchLower)
        ?? false
    )

    ||

    (
      p.variations
        ?.some(v =>
          v.toLowerCase()
           .includes(searchLower)
        )
        ?? false
    )

||

(
  p.markers
    ?.some(marker =>
      marker
        .toLowerCase()
        .includes(searchLower)
    )
    ?? false
)

);
    }
const getSafeTime = (date: any): number => {

  if (!date) return 0;

  // Firestore Timestamp
  if (typeof date.toDate === 'function') {
    return date.toDate().getTime();
  }

  // Date normal
  if (date instanceof Date) {
    return date.getTime();
  }

  // string/date convertível
  return new Date(date).getTime() || 0;
};
    // Sorting
    if (filters?.sortBy === 'popular') {
      prompts.sort((a, b) => (b.likes || 0) - (a.likes || 0));
    } else if (filters?.sortBy === 'oldest') {
      prompts.sort(
        (a, b) =>getSafeTime(a.createdAt) - getSafeTime(b.createdAt)
      );
    } else {
      prompts.sort(
        (a, b) => getSafeTime(b.createdAt) - getSafeTime(a.createdAt)
      );
    }

    return prompts;
  }

  try {
    const constraints = [];
    if (filters?.category) {
  constraints.push(
    where(
      'categoryName',
      '==',
      filters.category
    )
  );
}

    // Adicionar filtros de modo
    if (
  filters?.mode === 'personal'
  &&
  filters?.userId
) {

  constraints.push(
    where(
      'userId',
      '==',
      filters.userId
    )
  );

  constraints.push(
    where(
      'isPublic',
      '==',
      false
    )
  );

}
 else if (filters?.mode === 'global') {
      constraints.push(where('isPublic', '==', true));
    }

    const promptsQuery = query(
  collection(db, 'prompts'),
  ...(constraints as any),
  orderBy('createdAt', 'desc')
);

const querySnapshot =
  await getDocs(promptsQuery);

    let prompts: Prompt[] = querySnapshot.docs.map((doc) => ({
      ...(doc.data() as Omit<Prompt, 'id'>),
      id: doc.id,
    }));


console.log(
  prompts.map(p => ({
    id: p.id,
    isPublic: p.isPublic,
    createdAt: p.createdAt
  }))
);

    // Client-side filtering
    if (filters?.search) {
      const searchLower = filters.search.toLowerCase();
     prompts = prompts.filter(
  (p) =>

    p.content
      .toLowerCase()
      .includes(searchLower)

    ||

    (
      p.categoryName
        ?.toLowerCase()
        .includes(searchLower)
        ?? false
    )

    ||

    (
      p.identifier
        ?.toLowerCase()
        .includes(searchLower)
        ?? false
    )

    ||

    (
      p.observation
        ?.toLowerCase()
        .includes(searchLower)
        ?? false
    )

    ||

    (
      p.author
        ?.toLowerCase()
        .includes(searchLower)
        ?? false
    )

    ||

    (
      p.variations
        ?.some(v =>
          v.toLowerCase()
           .includes(searchLower)
        )
        ?? false
    )

||

(
  p.markers
    ?.some(marker =>
      marker
        .toLowerCase()
        .includes(searchLower)
    )
    ?? false
)

);
    }
const getSafeTime = (date: any): number => {

  if (!date) return 0;

  // Firestore Timestamp
  if (typeof date.toDate === 'function') {
    return date.toDate().getTime();
  }

  // Date normal
  if (date instanceof Date) {
    return date.getTime();
  }

  // string/date convertível
  return new Date(date).getTime() || 0;
};
    // Sorting
    if (filters?.sortBy === 'popular') {
      prompts.sort((a, b) => (b.likes || 0) - (a.likes || 0));
    } else if (filters?.sortBy === 'oldest') {
      prompts.sort(
        (a, b) => getSafeTime(a.createdAt) - getSafeTime(b.createdAt)
      );
    } else {
      prompts.sort(
        (a, b) => getSafeTime(b.createdAt) - getSafeTime(a.createdAt)
      );
    }

    return prompts;
  } catch (error) {
    console.error('Erro ao buscar prompts:', error);
    throw error;
  }
}

const sanitizePromptData = (
  data: Partial<Prompt>
): Partial<Prompt> => {

  return {

    ...data,

    isSensitive:
  typeof data.isSensitive === 'boolean'
    ? data.isSensitive
    : false,

    content:
      data.content
        ?.trim()
        .slice(0, 20000) || '',


        categoryId:
  data.categoryId
    ?.trim()
    .slice(0, 80) || '',

categoryName:
  data.categoryName
    ?.trim()
    .slice(0, 50) || '',

    author:
      data.author
        ?.trim()
        .slice(0, 60) || '',

    observation:
      data.observation
        ?.trim()
        .slice(0, 500) || '',
  };
};

export async function createPrompt(
  promptData: Omit<Prompt, 
  |'id'
  |'createdAt' 
  |'updatedAt'
  |'userId'
  |'author'
  |'isPublic'
   >,

  userId: string,
  isPublic: boolean = false
): Promise<string> {
  // Usa a função segura de salvamento
  const result = await savePrompt(promptData, userId, isPublic);

  if (!result.success) {
    throw new Error(result.error || 'Erro ao criar prompt');
  }

  return result.prompt!.id;
}

export async function updatePrompt(
  id: string,
  updates: Partial<Omit<Prompt,  'id' | 'createdAt'>>, adminId: string
): Promise<void> {
  if (!isFirebaseAvailable()) {
    console.log('Mock update - Firebase not configured');
    const index = mockPrompts.findIndex(p => p.id === id);
    if (index !== -1) {
      mockPrompts[index] = { ...mockPrompts[index], ...updates, updatedAt: new Date() };
    }
    return;
  }

  try {
    const docRef = doc(db, 'prompts', id);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: new Date(),
      
    });
  
await createAdminLog(
  'update_prompt',
  adminId,
  id
);

  } catch (error) {
    console.error('Erro ao atualizar prompt:', error);
    throw error;
  }
}

export type DeletePromptResult = {
  storageCleanupPending: boolean;
};

const deletePromptImage = async (
  imagePath: string
): Promise<boolean> => {
  const imageRef = ref(storage, imagePath);

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      await deleteObject(imageRef);
      return false;
    } catch (error) {
      const code = (error as { code?: string }).code;

      if (code === 'storage/object-not-found') {
        return false;
      }

      if (attempt === 1) {
        console.warn('Limpeza de imagem pendente:', imagePath, error);
        return true;
      }
    }
  }

  return true;
};

export async function deletePrompt(
  id: string,
  adminId: string
): Promise<DeletePromptResult> {

  if (!isFirebaseAvailable()) {

    console.log(
      'Mock delete - Firebase not configured'
    );

    const index =
      mockPrompts.findIndex(
        p => p.id === id
      );

    if (index !== -1) {
      mockPrompts.splice(index, 1);
    }

    return { storageCleanupPending: false };
  }

  try {

    const promptRef =
      doc(db, 'prompts', id);

    const promptSnap =
      await getDoc(promptRef);

    const imagePath = promptSnap.exists()
      ? promptSnap.data().imagePath
      : '';

    await deleteDoc(promptRef);

    const storageCleanupPending =
      typeof imagePath === 'string' && imagePath.length > 0
        ? await deletePromptImage(imagePath)
        : false;

    await createAdminLog(
      'delete_prompt',
      adminId,
      id
    );

    if (storageCleanupPending) {
      await createAdminLog(
        'prompt_media_cleanup_pending',
        adminId,
        id,
        { imagePath }
      );
    }

    return { storageCleanupPending };

  } catch (error) {

    console.error(
      'Erro ao deletar prompt:',
      error
    );

    throw error;
  }
}

// Categories
export async function getCategories(
  userId?: string
): Promise<Category[]> {
  try {
    // Busca todos os prompts para extrair categorias dinâmicas
    const categoriesRef =
  collection(db, 'categories');

const globalQuery = query(
  categoriesRef,

  where('type', '==', 'global'),

  where('isActive', '==', true)
);

const globalSnapshot =
  await getDocs(globalQuery);

let categories: Category[] =

  globalSnapshot.docs.map((docItem) => ({

    id: docItem.id,

    ...docItem.data(),

  })) as Category[];


// =====================================
// categorias pessoais
// =====================================

if (userId) {

  const personalQuery = query(

  categoriesRef,

  where('type', '==', 'personal'),

  where('ownerId', '==', userId),

  where('isActive', '==', true)
);

  const personalSnapshot =
    await getDocs(personalQuery);

  const personalCategories =

    personalSnapshot.docs.map((docItem) => ({

      id: docItem.id,

      ...docItem.data(),

    })) as Category[];

  categories = [
    ...categories,
    ...personalCategories
  ];
}

const hasTodos =

  categories.some(

    cat => cat.name === 'Todos'
  );

if (!hasTodos) {

  categories.unshift({

    id: 'todos',

    name: 'Todos',

    color: '#6B7280'
  });
}

return categories;
  } catch (error) {
    console.error('Erro ao buscar categorias:', error);
    // Fallback para categorias mock em caso de erro
    return [...mockCategories];
  }
}

export async function getAllCategories(): Promise<Category[]> {

  try {

    const snapshot =
      await getDocs(

        query(

          collection(db, 'categories'),

          where(
            'type',
            'in',
            ['global', 'personal']
          )

        )

      );

    return snapshot.docs.map(
      (docItem) => ({
        id: docItem.id,
        ...docItem.data(),
      })
    ) as Category[];

  } catch (error) {

    console.error(error);

    return [];
  }
}

export async function getUsers(): Promise<UserData[]> {

  try {

    const snapshot =
      await getDocs(
        collection(
          db,
          'users'
        )
      );

    return snapshot.docs.map(
      (docItem) => ({

        id: docItem.id,

        ...docItem.data(),

      } as UserData)
    );

  } catch (error) {

    console.error(
      'Erro ao buscar usuários:',
      error
    );

    return [];
  }
}

// Futura refatoração completa do painel admin
export async function getUserRole(
  uid: string
): Promise<string | null> {

  try {

    const docSnap =
      await getDoc(
        doc(
          db,
          'users',
          uid
        )
      );

    if (!docSnap.exists()) {
      return null;
    }

    return (
      docSnap.data().role
      || null
    );

  } catch (error) {

    console.error(
      'Erro ao buscar role:',
      error
    );

    return null;
  }
}

export async function updateUserRole(
  uid: string,
  role: 'user' | 'admin'
): Promise<void> {
  try {

    await updateDoc(
      doc(db, 'users', uid),
      {
        role
      }
    );

    const adminId = auth.currentUser?.uid;
    if (adminId) {
      await createAdminLog('update_user_role', adminId, undefined, {
        targetUserId: uid,
        role,
      });
    }

  } catch (error) {

    console.error(
      'Erro ao atualizar cargo:',
      error
    );

    throw error;
  }
}

export async function updateAccountStatus(

  uid: string,

  accountStatus:
    typeof ACCOUNT_STATUS[keyof typeof ACCOUNT_STATUS]

) {

  try {
    const userRef = doc(db, 'users', uid);

    if (accountStatus === ACCOUNT_STATUS.ACTIVE) {
      const userSnap = await getDoc(userRef);
      const expiresAt = userSnap.data()?.subscriptionExpiresAt;
      const expiresAtDate = expiresAt?.toDate?.();

      if (!expiresAtDate || expiresAtDate <= new Date()) {
        throw new Error('ACTIVE_STATUS_REQUIRES_VALID_SUBSCRIPTION');
      }
    }

    await updateDoc(

      userRef,

      {

        accountStatus

      }

    );

    const adminId = auth.currentUser?.uid;
    if (adminId) {
      await createAdminLog('update_account_status', adminId, undefined, {
        targetUserId: uid,
        accountStatus,
      });
    }

  } catch (error) {

    console.error(
      'Erro ao alterar status:',
      error
    );

    throw error;
  }
}

/**
 * Aprova um usuário e ativa sua assinatura
 * @param uid UID do usuário
 * @param adminUid UID do admin que aprovou
 * NOTA: Usuário deve estar no estado PENDING. Aprovação anterior é perdida.
 */
export async function approveUser(
  uid: string,
  adminUid: string
): Promise<void> {
  try {
    const now = new Date();
    const thirtyDaysFromNow = new Date(now);
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + DEFAULT_SUBSCRIPTION_DAYS);

    await updateDoc(
      doc(db, 'users', uid),
      {
        accountStatus: ACCOUNT_STATUS.ACTIVE,
        approvedAt: serverTimestamp(),
        approvedBy: adminUid,
        subscriptionExpiresAt: Timestamp.fromDate(thirtyDaysFromNow),
        lastStatusChangeAt: serverTimestamp(),
        lastStatusChangedBy: adminUid,
        accountStatusReason: null,
        // Subscription Foundation - FASE 5
        subscriptionStartedAt: Timestamp.fromDate(now),
        subscriptionPlan: DEFAULT_PLAN,
        billingCycle: DEFAULT_BILLING_CYCLE,
        renewalCount: INITIAL_RENEWAL_COUNT,
        nextRenewalAt: Timestamp.fromDate(thirtyDaysFromNow),
      }
    );
    await createAdminLog('approve_user', adminUid, undefined, {
      targetUserId: uid,
    });
  } catch (error) {
    console.error('Erro ao aprovar usuário:', error);
    throw error;
  }
}

/**
 * Renova a assinatura de um usuário
 * @param uid UID do usuário
 * @param adminUid UID do admin que renovou
 */
export async function renewSubscription(
  uid: string,
  adminUid: string
): Promise<void> {
  try {
    // Buscar contagem atual de renovações
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      throw new Error(`Usuário ${uid} não encontrado`);
    }

    const now = new Date();
    const thirtyDaysFromNow = new Date(now);
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + DEFAULT_SUBSCRIPTION_DAYS);

    await updateDoc(
      userRef,
      {
        accountStatus: ACCOUNT_STATUS.ACTIVE,
        subscriptionExpiresAt: Timestamp.fromDate(thirtyDaysFromNow),
        lastRenewedAt: serverTimestamp(),
        lastRenewedBy: adminUid,
        lastStatusChangeAt: serverTimestamp(),
        lastStatusChangedBy: adminUid,
        accountStatusReason: null,
        // Subscription Foundation - FASE 6
        renewalCount: increment(1),
        nextRenewalAt: Timestamp.fromDate(thirtyDaysFromNow),
        subscriptionPlan: userSnap.data()?.subscriptionPlan || DEFAULT_PLAN,
        billingCycle: userSnap.data()?.billingCycle || DEFAULT_BILLING_CYCLE,
        subscriptionStartedAt: userSnap.data()?.subscriptionStartedAt
          || Timestamp.fromDate(now),
      }
    );
    await createAdminLog('renew_subscription', adminUid, undefined, {
      targetUserId: uid,
    });
  } catch (error) {
    console.error('Erro ao renovar assinatura:', error);
    throw error;
  }
}

/**
 * Suspende a conta de um usuário
 * @param uid UID do usuário
 * @param adminUid UID do admin que suspendeu
 */
export async function suspendUser(
  uid: string,
  adminUid: string
): Promise<void> {
  try {
    await updateDoc(
      doc(db, 'users', uid),
      {
        accountStatus: ACCOUNT_STATUS.SUSPENDED,
        suspendedAt: serverTimestamp(),
        suspendedBy: adminUid,
        lastStatusChangeAt: serverTimestamp(),
        lastStatusChangedBy: adminUid,
      }
    );
    await createAdminLog('suspend_user', adminUid, undefined, {
      targetUserId: uid,
    });
  } catch (error) {
    console.error('Erro ao suspender usuário:', error);
    throw error;
  }
}

/**
 * Bloqueia a conta de um usuário com um motivo
 * @param uid UID do usuário
 * @param reason Motivo do bloqueio
 * @param adminUid UID do admin que bloqueou
 */
export async function blockUser(
  uid: string,
  reason: string,
  adminUid: string
): Promise<void> {
  try {
    await updateDoc(
      doc(db, 'users', uid),
      {
        accountStatus: ACCOUNT_STATUS.BLOCKED,
        accountStatusReason: reason,
        blockedAt: serverTimestamp(),
        blockedBy: adminUid,
        lastStatusChangeAt: serverTimestamp(),
        lastStatusChangedBy: adminUid,
      }
    );
    await createAdminLog('block_user', adminUid, undefined, {
      targetUserId: uid,
    });
  } catch (error) {
    console.error('Erro ao bloquear usuário:', error);
    throw error;
  }
}

// Verifique se a categoria existe; caso contrário, crie-a. Para categorias pessoais, verifica se o usuário já tem uma categoria com esse nome. Para categorias públicas, verifica se a categoria global existe.

export async function saveLegalAcceptance(
  uid: string
): Promise<void> {
  try {
    const documents: LegalAcceptance['documents'] = {
      manifesto: manifesto.version,
      conduct: codeOfConduct.version,
      terms: termsOfUse.version,
      privacy: privacyPolicy.version,
      subscription: subscriptionPolicy.version,
    };

    const legalAcceptance: Omit<LegalAcceptance, 'acceptedAt'> & {
      acceptedAt: ReturnType<typeof serverTimestamp>;
    } = {
      accepted: true,
      acceptedAt: serverTimestamp(),
      documents,
    };

    await updateDoc(
      doc(db, 'users', uid),
      {
        legalAcceptance,
      }
    );
  } catch (error) {
    console.error(
      'Erro ao salvar aceite jurÃ­dico:',
      error
    );

    throw error;
  }
}

type EnsureCategoryResult = {
  categoryId: string;
  categoryName: string;
};

const normalizeCategorySearchName = (name: string): string =>
  name.trim().toLowerCase();

const getLegacySearchNameVariants = (searchName: string): string[] => {
  const legacyUppercase = searchName.toUpperCase();

  return legacyUppercase === searchName
    ? [searchName]
    : [searchName, legacyUppercase];
};

const getCategoryDocumentId = (
  type: 'global' | 'personal',
  searchName: string,
  ownerId?: string
): string => {
  const scope = ownerId ? `${ownerId}_` : '';
  return `${type}_${scope}${encodeURIComponent(searchName)}`;
};

export async function ensureCategoryExists(
  categoryName: string,
  userId: string,
  isPublic: boolean
): Promise<EnsureCategoryResult> {

const normalizedName = normalizeCategorySearchName(categoryName);
  if (!isFirebaseAvailable()) {
    const categoryExists = mockCategories.some(cat => cat.name === normalizedName);
    if (!categoryExists && normalizedName !== 'todos') {
      mockCategories.push({
        id: Date.now().toString(),
        name: categoryName,
        color: '#6B7280'
      });
    }
    return {

  categoryId: categoryName,

  categoryName

};
  }

  try {

  const categoriesRef =
    collection(db, 'categories');

  // =====================================
  // PROMPT PÚBLICO
  // =====================================


  if (isPublic) {

    const q = query(
      categoriesRef,
      where('searchName', 'in', getLegacySearchNameVariants(normalizedName)),
      where('type', '==', 'global')
    );

    const querySnapshot =
      await getDocs(q);

    // categoria global precisa existir
    if (querySnapshot.empty) {


      throw new Error(
        'Categoria pública inválida.'
      );

      
    }

    

   const categoryDoc =
  querySnapshot.docs[0];

return {

  categoryId: categoryDoc.id,

  categoryName:
    categoryDoc.data().name

};
  }

  // =====================================
  // PROMPT PRIVADO
  // =====================================

  const q = query(
    categoriesRef,
    where('searchName', 'in', getLegacySearchNameVariants(normalizedName)),
    where('ownerId', '==', userId),
    where('type', '==', 'personal')
  );

  const querySnapshot =
    await getDocs(q);

  // categoria pessoal não existe → cria
  // categoria pessoal não existe → cria
if (querySnapshot.empty) {

  const docRef = doc(
    categoriesRef,
    getCategoryDocumentId('personal', normalizedName, userId)
  );

  const categorySnapshot = await getDoc(docRef);

  if (!categorySnapshot.exists()) {
    await setDoc(docRef, {
      name: categoryName.trim(),
      searchName: normalizedName,
      type: 'personal',
      ownerId: userId,
      color: '#6B7280',
      isActive: true,
      createdAt: serverTimestamp(),
    });
  } else if (categorySnapshot.data().isActive === false) {
    await updateDoc(docRef, { isActive: true });
  }

  return {

    categoryId: docRef.id,

    categoryName: categorySnapshot.exists()
      ? categorySnapshot.data().name
      : categoryName.trim()

  };
}

// categoria já existe
const categoryDoc =
  querySnapshot.docs[0];

if (categoryDoc.data().isActive === false) {
  await updateDoc(categoryDoc.ref, { isActive: true });
}

return {

  categoryId: categoryDoc.id,

  categoryName:
    categoryDoc.data().name

};
  

} catch (error) {

  console.error(
    'Erro ao verificar/criar categoria:',
    error
  );

  throw error;
}
}

export async function createGlobalCategory(
  name: string
): Promise<string> {

  if (!isFirebaseAvailable()) {

    console.log(
      'Mock create global category - Firebase not configured'
    );

    const newId =
      Date.now().toString();

    mockCategories.push({

      id: newId,

      name,

      type: 'global',

      isActive: true,

      color: '#6B7280'

    });

    return newId;
  }

  try {


  const normalizedName = name.trim();
  const searchName = normalizeCategorySearchName(normalizedName);

  if (!normalizedName) {
  throw new Error(
    'Nome da categoria inválido.'
  );
}

  
  const existingQuery = query(
  collection(db, 'categories'),
  where('type', '==', 'global'),
  where(
  'searchName',
  'in',
  getLegacySearchNameVariants(searchName)
)
);

const existingSnapshot =
  await getDocs(existingQuery);

if (!existingSnapshot.empty) {
  throw new Error(
    'Já existe uma categoria global com esse nome.'
  );
}


    const docRef = doc(
      collection(db, 'categories'),
      getCategoryDocumentId('global', searchName)
    );

    const categorySnapshot = await getDoc(docRef);

    if (!categorySnapshot.exists()) {
      await setDoc(docRef, {

        name: normalizedName,

        searchName,

        type: 'global',

        isActive: true,

        color: '#6B7280',

        createdAt:
          serverTimestamp()

      });

      const adminId = auth.currentUser?.uid;
      if (adminId) {
        await createAdminLog('create_global_category', adminId, undefined, {
          targetCategoryId: docRef.id,
        });
      }
    }

    return docRef.id;

  } catch (error) {

    console.error(
      'Erro ao criar categoria global:',
      error
    );

    throw error;
  }
}

export async function updateCategoryStatus(
  categoryId: string,
  isActive: boolean
): Promise<void> {

  if (!isFirebaseAvailable()) {

    const category =
      mockCategories.find(
        cat => cat.id === categoryId
      );

    if (category) {
      category.isActive =
        isActive;
    }

    return;
  }

  try {

    await updateDoc(

      doc(
        db,
        'categories',
        categoryId
      ),

      {
        isActive,
      }

    );

    const adminId = auth.currentUser?.uid;
    if (adminId) {
      await createAdminLog('update_category_status', adminId, undefined, {
        targetCategoryId: categoryId,
        isActive: String(isActive),
      });
    }

  } catch (error) {

    console.error(
      'Erro ao atualizar categoria:',
      error
    );

    throw error;
  }
}

export async function deleteCategory(categoryId: string): Promise<void> {
  if (!isFirebaseAvailable()) {
    console.log('Mock delete category - Firebase not configured');
    const category = mockCategories.find((cat) => cat.id === categoryId);
    if (category) {
      category.isActive = false;
    }

    return;
  }

  try {

   // BUSCA PROMPTS
    const promptsQuery = query(
  collection(db, 'prompts'),
  where(
    'categoryId',
    '==',
    categoryId
  )
);

    const promptSnapshot = await getDocs(promptsQuery);

    if (!promptSnapshot.empty) {

  throw new Error(
    'CATEGORY_HAS_PROMPTS'
  );

}

  } catch (error) {
    console.error('Erro ao deletar categoria:', error);
    throw error;
  }

  // Mantém a referência histórica de prompts e retira a categoria das listas ativas.
  await updateDoc(doc(db, 'categories', categoryId), {
    isActive: false,
  });

  const adminId = auth.currentUser?.uid;
  if (adminId) {
    await createAdminLog('deactivate_category', adminId, undefined, {
      targetCategoryId: categoryId,
    });
  }

}

export const createAdminLog = async (

  action: string,

  adminId: string,

  targetPromptId?: string,

  metadata?: Record<string, string>

) => {

  try {

    await addDoc(
      collection(db, 'adminLogs'),

      {
        action,
        adminId,
        targetPromptId:
          targetPromptId || null,

        ...(metadata ? { metadata } : {}),

        createdAt: serverTimestamp(),
      }
    );

  } catch (error) {

    console.error(
      'Erro ao criar admin log:',
      error
    );
  }
};
export async function hidePrompt(
  id: string
): Promise<void> {

  try {

    const promptRef =
      doc(db, 'prompts', id);

    await updateDoc(promptRef, {

      isHidden: true,

      updatedAt: serverTimestamp(),
    });

    const adminId = auth.currentUser?.uid;
    if (adminId) {
      await createAdminLog('hide_prompt', adminId, id);
    }

  } catch (error) {

    console.error(
      'Erro ao ocultar prompt:',
      error
    );

    throw error;
  }
}

export async function restorePrompt(
  id: string
): Promise<void> {

  try {

    const promptRef =
      doc(db, 'prompts', id);

    await updateDoc(promptRef, {

      isHidden: false,

      updatedAt: new Date(),
    });

    const adminId = auth.currentUser?.uid;
    if (adminId) {
      await createAdminLog('restore_prompt', adminId, id);
    }

  } catch (error) {

    console.error(
      'Erro ao restaurar prompt:',
      error
    );

    throw error;
  }
}

/**
 * Salva/cria documento do usuário no Firestore
 * CRÍTICO: Usuário autenticado sem documento Firestore é considerado estado inválido.
 * Esta função DEVE ser executada após toda autenticação.
 * Se falhar, o usuário não pode ser considerado autenticado.
 * @param user Firebase User object
 * @throws Error se falhar ao criar/atualizar documento
 */
export async function saveUser(
  user: User
) {
  console.log(
  'SAVE USER INICIADO'
);

  try {

    const userRef =
      doc(db, 'users', user.uid);

    const userSnap =
      await getDoc(userRef);

    if (!userSnap.exists()) {

      console.log(
  'CRIANDO USER'
);
      await setDoc(userRef, {

      uid: user.uid,
      name:user.displayName
      || 'Usuário',
      email:user.email || '',
      role: 'user',
      accountStatus:ACCOUNT_STATUS.PENDING,
      createdAt:serverTimestamp(),
      photoURL:user.photoURL || '',
      lastLogin:serverTimestamp(),
      approvedAt: null,
      approvedBy: null,
      subscriptionExpiresAt: null,
      accountStatusReason: null,
      lastRenewedAt: null,
      lastRenewedBy: null,
      blockedAt: null,
      blockedBy: null,
      suspendedAt: null,
      suspendedBy: null,
      lastStatusChangeAt: null,
      lastStatusChangedBy: null,
      // Subscription Foundation - FASE 1
      subscriptionPlan: null,
      billingCycle: null,
      renewalCount: 0,
      subscriptionStartedAt: null,
      nextRenewalAt: null,
      });
      console.log(
  'USER CRIADO'
);

    } else {

      await updateDoc(userRef, {

        lastLogin:
          serverTimestamp(),
      });
    }

  } catch (error) {

    console.error(
      'Erro ao salvar usuário:',
      error
    );
    
    // CRÍTICO: Propagar erro para que autenticação falhe
    // Usuário autenticado sem documento Firestore é estado inválido
    throw error;
  }
}

export type UserPage = {
  users: UserData[];
  cursor: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
};

export async function getUsersPage(
  pageSize = 50,
  cursor: QueryDocumentSnapshot<DocumentData> | null = null,
): Promise<UserPage> {
  const constraints: QueryConstraint[] = [orderBy('createdAt', 'desc')];
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(pageSize + 1));
  const snapshot = await getDocs(query(collection(db, 'users'), ...constraints));
  const docs = snapshot.docs.slice(0, pageSize);
  return {
    users: docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }) as UserData),
    cursor: docs[docs.length - 1] ?? null,
    hasMore: snapshot.docs.length > pageSize,
  };
}

export type PromptPage = {
  prompts: Prompt[];
  cursor: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
};

const matchesPromptSearch = (prompt: Prompt, search: string) => {
  const searchLower = search.toLowerCase();
  return prompt.content.toLowerCase().includes(searchLower)
    || prompt.categoryName?.toLowerCase().includes(searchLower)
    || prompt.identifier?.toLowerCase().includes(searchLower)
    || prompt.observation?.toLowerCase().includes(searchLower)
    || prompt.author?.toLowerCase().includes(searchLower)
    || prompt.variations?.some((variation) => variation.toLowerCase().includes(searchLower))
    || prompt.markers?.some((marker) => marker.toLowerCase().includes(searchLower));
};

export async function getPromptsPage(
  filters: {
    category?: string;
    search?: string;
    sortBy?: 'newest' | 'oldest' | 'popular';
    mode?: 'global' | 'personal';
    userId?: string;
  },
  pageSize = 24,
  cursor: QueryDocumentSnapshot<DocumentData> | null = null,
): Promise<PromptPage> {
  const sortBy = filters.sortBy ?? 'newest';
  const constraints: QueryConstraint[] = [];
  if (filters.category) constraints.push(where('categoryName', '==', filters.category));
  if (filters.mode === 'personal' && filters.userId) {
    constraints.push(where('userId', '==', filters.userId), where('isPublic', '==', false));
  } else if (filters.mode === 'global') {
    constraints.push(where('isPublic', '==', true));
  }
  if (sortBy === 'popular') {
    constraints.push(orderBy('likes', 'desc'), orderBy('createdAt', 'desc'));
  } else {
    constraints.push(orderBy('createdAt', sortBy === 'oldest' ? 'asc' : 'desc'));
  }
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(pageSize + 1));
  const snapshot = await getDocs(query(collection(db, 'prompts'), ...constraints));
  const docs = snapshot.docs.slice(0, pageSize);
  const prompts = docs.map((docItem) => ({ ...(docItem.data() as Omit<Prompt, 'id'>), id: docItem.id }));
  return {
    prompts: filters.search ? prompts.filter((prompt) => matchesPromptSearch(prompt, filters.search!)) : prompts,
    cursor: docs[docs.length - 1] ?? null,
    hasMore: snapshot.docs.length > pageSize,
  };
}
